import React from "react";

/**
 * Centralised responsive breakpoints for the design-system app shell (FR-011).
 *
 * Single source of truth for the navigation presentation switch:
 *   - mobile  (< 768px)        → header bar + slide-in drawer
 *   - tablet  (768–1024px)     → collapsed icon rail
 *   - desktop (>= 1024px)      → expandable icon rail (72 ↔ 248)
 */
export const BREAKPOINTS = {
  /** Below this width we present the mobile header + drawer. */
  mobile: 768,
  /** At/above this width the rail can expand to show labels. */
  desktop: 1024,
} as const;

export type Breakpoint = "mobile" | "tablet" | "desktop";

export const getBreakpoint = (width: number): Breakpoint => {
  if (width < BREAKPOINTS.mobile) return "mobile";
  if (width < BREAKPOINTS.desktop) return "tablet";
  return "desktop";
};

const readWidth = (): number => (typeof window === "undefined" ? BREAKPOINTS.desktop : window.innerWidth);

const BreakpointContext = React.createContext<Breakpoint>("desktop");

/**
 * Coalesces resize/orientation events to one sync per animation frame and only
 * commits React state when the breakpoint bucket actually changes. Avoids
 * redundant renders while the user drags a window edge within the same bucket.
 */
const useBreakpointState = (): Breakpoint => {
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>(() => getBreakpoint(readWidth()));

  React.useEffect(() => {
    let frame = 0;

    const sync = (): void => {
      frame = 0;
      const next = getBreakpoint(readWidth());
      setBreakpoint((prev) => (prev === next ? prev : next));
    };

    const scheduleSync = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(sync);
      }
    };

    sync();
    window.addEventListener("resize", scheduleSync, { passive: true });
    window.addEventListener("orientationchange", scheduleSync);
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("orientationchange", scheduleSync);
    };
  }, []);

  return breakpoint;
};

/** Mount once at the app-shell root so resize listeners are not duplicated. */
export const BreakpointProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const breakpoint = useBreakpointState();
  return React.createElement(BreakpointContext.Provider, { value: breakpoint }, children);
};

/**
 * Reactive breakpoint from the nearest {@link BreakpointProvider}. When no
 * provider is mounted (legacy routes, isolated unit tests) the default is
 * `desktop`.
 */
export const useBreakpoint = (): Breakpoint => React.useContext(BreakpointContext);

/** True when the user has requested reduced motion (FR-013, FR-022). */
export const usePrefersReducedMotion = (): boolean => {
  const query = "(prefers-reduced-motion: reduce)";
  const read = (): boolean =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false;

  const [reduced, setReduced] = React.useState<boolean>(read);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setReduced(mql.matches);
    onChange();
    // Safari < 14 only supports the deprecated addListener API.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
};
